import { useEffect, useReducer, useState } from "react";
import { Modal, ModalBody, ModalFooter, ModalHeader } from "react-bootstrap";
import Select from "react-select";
import { optionSelector } from "../../../../config/apiSPA";
/**
 * @typedef {Object} CompetencyData
 * @property {number} tel2CompetencyId
 * @property {number} trainingParticipantId
 * @property {number} postTest
 * @property {number} preTest
 * @property {number} practiceTest
 * @property {number} passingScore
 * @property {string} result
 * @property {boolean} resultOverride
 * @property {string} remark
 */

/**
 * @typedef {Object} ModalProps
 * @property {boolean} show
 * @property {() => void} onHide
 * @property {string} [title]
 * @property {CompetencyData} [data]
 * @property {(data: CompetencyData) => void} submitClick
 */

/**
 * @param {ModalProps} props
 * @returns {JSX.Element}
 */

const resultOptions = [
  {
    value: "NOT YET",
    label: "NOT YET",
  },
  {
    value: "PASS",
    label: "PASS",
  },
  {
    value: "FAIL",
    label: "FAIL",
  },
];
const formDataInitialState = {
  tel2CompetencyId: null, //untuk new
  tel2EvaluationId: null, // untuk edit
  trainingParticipantId: null,
  preTest: null,
  postTest: null,
  practiceTest: null,
  passingScore: null,
  result: null,
  resultOverride: false,
  remark: "",
};
const scoreFields = ["preTest", "postTest", "practiceTest"];

function ModalEditAddEvaluationParticipant({ show, onHide, title = "Add Participant Evaluation", isEdit = false, data, submitClick }) {
  const [formData, setFormData] = useState(formDataInitialState);
  const [participantOptions, setParticipantOptions] = useState([]);
  const [isLoadingParticipant, setIsLoadingParticipant] = useState(false);

  // Reset form ketika modal dibuka / data berubah
  useEffect(() => {
    if (!show) return;

    setFormData({
      tel2CompetencyId: data?.tel2CompetencyId ?? null,
      tel2EvaluationId: data?.tel2EvaluationId ?? null,
      trainingParticipantId: data?.trainingParticipantId ?? null,
      preTest: data?.preTest ?? null,
      postTest: data?.postTest ?? null,
      practiceTest: data?.practiceTest ?? null,
      passingScore: data?.passingScore ?? null,
      result: data?.result ?? null,
      resultOverride: data?.resultOverride ?? false,
      remark: data?.remark ?? "",
    });
  }, [show, data]);

  // Load competency
  useEffect(() => {
    if (!show) return;

    const loadCompetency = async () => {
      try {
        setIsLoadingParticipant(true);
        const url = isEdit ? `/tel2-participant-edit?tel2CompetencyId=${data.tel2CompetencyId}` : `/tel2-participant?tel2CompetencyId=${data.tel2CompetencyId}`;
        const res = await optionSelector(url);
        setParticipantOptions(res);
      } catch (error) {
        console.error("Failed to load participant:", error);
        setParticipantOptions([]);
      } finally {
        setIsLoadingParticipant(false);
      }
    };

    loadCompetency();
  }, [show, isEdit, data.tel2CompetencyId]);

  const handleChange = (event) => {
    const { id, value } = event.target;
    const newValue = scoreFields.includes(id) ? (value === "" ? null : Number(value)) : value;
    setFormData((prev) => ({
      ...prev,
      [id]: newValue,

      ...(id === "postTest" && {
        result: value === "" ? "NOT YET" : Number(value) >= Number(prev.passingScore) ? "PASS" : "FAIL",
        resultOverride: false,
      }),
    }));
  };

  const handleParticipantChange = (selectedOption) => {
    setFormData((prev) => ({
      ...prev,
      trainingParticipantId: selectedOption?.value ?? null,
    }));
  };
  const handleResultChange = (selectedOption) => {
    setFormData((prev) => ({
      ...prev,
      result: selectedOption?.value ?? null,
      resultOverride: true,
    }));
  };

  const handleClickSubmit = () => {
    if (!isFormValid) return;

    submitClick(
      {
        tel2CompetencyId: formData.tel2CompetencyId,
        tel2EvaluationId: formData.tel2EvaluationId,
        trainingParticipantId: formData.trainingParticipantId,
        preTest: formData.preTest,
        postTest: formData.postTest,
        practiceTest: formData.practiceTest,
        result: formData.result,
        resultOverride: formData.resultOverride,
        remark: formData.remark,
      },
      isEdit,
    );
  };

  const isFormValid = formData.tel2CompetencyId !== null && formData.trainingParticipantId !== null && formData.passingScore !== "" && formData.passingScore >= 0 && formData.passingScore <= 100;

  const selectedParticipant = participantOptions.find((option) => option.value === formData.trainingParticipantId) ?? null;
  const selectedResult = resultOptions.find((option) => option.value === formData.result) ?? null;
  const isParticipantDisabled = isLoadingParticipant || (isEdit ?? false);
  return (
    <Modal show={show} onHide={onHide}>
      <ModalHeader onHide={onHide}>{title}</ModalHeader>
      <ModalBody>
        {/* Competency */}
        <div className="form-group mb-3">
          <label className="form-label" htmlFor="trainingParticipantId">
            Participants
          </label>

          <Select
            inputId="trainingParticipantId"
            classNamePrefix="react-select"
            isSearchable
            isClearable
            isLoading={isLoadingParticipant}
            isDisabled={isParticipantDisabled}
            options={participantOptions}
            value={selectedParticipant}
            onChange={handleParticipantChange}
            placeholder={isLoadingParticipant ? "Loading participant..." : "Select participant..."}
            noOptionsMessage={() => "Participant not found"}
          />
        </div>

        {/* Pre Test */}
        <div className="form-group mb-3">
          <label className="form-label" htmlFor="preTest">
            Pre Test
          </label>

          <input type="number" className="form-control" id="preTest" min={0} max={100} value={formData.preTest ?? ""} onChange={handleChange} />
        </div>
        {/* Post Test */}
        <div className="form-group mb-3">
          <label className="form-label" htmlFor="postTest">
            Post Test
          </label>

          <input type="number" className="form-control" id="postTest" min={0} max={100} value={formData.postTest ?? ""} onChange={handleChange} />
        </div>
        {/* Practice Test */}
        <div className="form-group mb-3">
          <label className="form-label" htmlFor="practiceTest">
            Practice Test
          </label>

          <input type="number" className="form-control" id="practiceTest" min={0} max={100} value={formData.practiceTest ?? ""} onChange={handleChange} />
        </div>
        <div className="form-group mb-3">
          <label className="form-label" htmlFor="result">
            Result (Base On Post Test Passing Score : {formData.passingScore})
          </label>

          <Select inputId="result" classNamePrefix="react-select" isSearchable options={resultOptions} value={selectedResult} onChange={handleResultChange} placeholder={"Select result..."} noOptionsMessage={() => "Competency not found"} />
        </div>
        {/* Remark */}
        <div className="form-group mb-3">
          <label className="form-label" htmlFor="remark">
            Remark
          </label>

          <textarea className="form-control" id="remark" value={formData.remark} onChange={handleChange} />
        </div>
      </ModalBody>

      <ModalFooter>
        <button onClick={onHide} type="button" className="btn btn-secondary">
          Cancel
        </button>

        <button onClick={handleClickSubmit} type="button" className="btn btn-outline-theme" disabled={!isFormValid}>
          Submit
        </button>
      </ModalFooter>
    </Modal>
  );
}

export default ModalEditAddEvaluationParticipant;
